import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { mobile, visuallyHidden } from "clutch/src/Style/style.mjs";

import { MoreGamesDropdown } from "@/app/AppShellGamesDropdown.jsx";
import {
  GAME_BOX_ICONS,
  GAME_COLORS,
  GAME_PREFERRED_NAME_MAP,
  GAME_SHORT_NAMES,
  GAMES_TO_DISPLAY,
} from "@/app/constants.mjs";
import BlitzLogo from "@/inline-assets/blitz-logo.svg";
import BlitzWordmark from "@/inline-assets/wordmark.svg";
import { useGameSymbol } from "@/util/game-route.mjs";

const GamesBarWrapper = styled("div")`
  display: flex;
  align-items: center;
  gap: var(--sp-px);
  align-self: stretch;
  background: var(--shade9);
`;

const BlitzLogoWrapper = styled("a")`
  display: flex;
  padding: 0 var(--sp-5) 0 var(--sp-4);
  align-items: center;
  gap: var(--sp-2);
  align-self: stretch;
  border-bottom: solid 1px var(--shade6-50);

  &.active {
    background: var(--shade8-75);
    box-shadow: -1px 0px 0px 0px rgba(39, 42, 48, 0.5);
    border-bottom: solid 1px transparent;
  }

  .icon {
    display: flex;
    width: var(--sp-4);
    height: var(--sp-4);
    padding: 0 var(--sp-px);
    justify-content: center;
    align-items: center;

    svg {
      width: var(--sp-4_5);
      height: var(--sp-4_5);
      flex-shrink: 0;
    }
  }

  .wordmark {
    width: calc(var(--sp-1) * 13.25);
    height: var(--sp-2_5);
  }

  ${mobile} {
    display: none;
  }
`;

const GameLinksWrapper = styled("nav")`
  flex: 1;
  white-space: nowrap;
  overflow: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const GameLinksList = styled("ul")`
  list-style: none;

  li {
    display: inline-block;
  }
`;

const GameLink = styled<
  "a",
  CSSVariables<{
    "--game-accent-color": string;
  }>
>("a")`
  display: flex;
  padding: var(--sp-2) var(--sp-3);
  align-items: center;
  gap: var(--sp-2);
  background: var(--shade9);
  box-shadow: -1px 0px 0px 0px var(--shade6-50);
  border-bottom: solid 1px var(--shade6-50);

  span {
    color: var(--shade3);
    white-space: nowrap;
    transition: color 150ms cubic-bezier(0, 0, 0.15, 1);

    ${mobile} {
      ${visuallyHidden}
    }
  }

  svg {
    width: var(--sp-5);
    height: var(--sp-5);
    border-radius: 5px;
    background: var(--shade3-15);

    path,
    polygon {
      fill: var(--shade3);
    }

    transition: fill 150ms cubic-bezier(0, 0, 0.15, 1);
  }

  &:hover,
  &:focus,
  &:active,
  &.active {
    background: var(--shade8);
    box-shadow:
      -1px 0px 0px 0px rgba(39, 42, 48, 0.5),
      0px 1px 0px 0px var(--shade8);
    border-bottom: solid 1px transparent;

    svg {
      background: var(--game-accent-color);

      path,
      polygon {
        fill: var(--shade9);
      }
    }
  }

  &:hover {
    span {
      color: var(--shade1);
    }
  }

  &:focus,
  &:active,
  &.active {
    span {
      color: var(--shade0);
    }
  }

  &:focus {
    outline-offset: -2px;
  }
`;

const HOME_HREF = "/";

export default React.memo(MainNavGamesBar);

function MainNavGamesBar({ currentPath }) {
  const { t } = useTranslation();
  const selectedGameSymbol = useGameSymbol();

  return (
    <GamesBarWrapper className="games-bar">
      <BlitzLogoWrapper
        href={HOME_HREF}
        className={currentPath === HOME_HREF ? "active" : ""}
      >
        <div className="icon">
          <BlitzLogo className="original-colors" />
        </div>

        <BlitzWordmark className="wordmark" />
      </BlitzLogoWrapper>
      <GameLinksWrapper className="games" aria-label="Primary">
        <GameLinksList>
          {GAMES_TO_DISPLAY.map((gameSymbol) => {
            // The only reason GAME_COLORS is used here is because
            // we dont care about the context of the current game
            const GameColor = GAME_COLORS[gameSymbol];
            const GameIcon = GAME_BOX_ICONS[gameSymbol];
            return (
              <li key={GAME_SHORT_NAMES[gameSymbol]}>
                <GameLink
                  className={selectedGameSymbol === gameSymbol ? "active" : ""}
                  href={`/${GAME_SHORT_NAMES[gameSymbol]}`}
                  style={{ "--game-accent-color": GameColor }}
                  aria-current={selectedGameSymbol === gameSymbol ? true : null}
                >
                  {GameIcon && <GameIcon aria-hidden="true" />}

                  <span className="type-callout--semi">
                    {t(...GAME_PREFERRED_NAME_MAP[gameSymbol])}
                  </span>
                </GameLink>
              </li>
            );
          })}
        </GameLinksList>
      </GameLinksWrapper>

      <MoreGamesDropdown />
    </GamesBarWrapper>
  );
}
